import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { functions, db } from '../../firebase';
import { PRICING } from '../../utils/subscriptionUtils';
import { CreditCard, Shield, Loader, Users, Minus, Plus } from 'lucide-react';

export default function Checkout({ user, companyId }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userCount, setUserCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');
  const [bundleEligible, setBundleEligible] = useState(false);

  const pricePerUser = bundleEligible ? PRICING.BUNDLE : PRICING.STANDALONE;
  const totalAmount = userCount ? userCount * pricePerUser : 0;
  const formatPrice = (value) => `£${value.toFixed(2)}`;

  useEffect(() => {
    const loadCompanyData = async () => {
      try {
        if (!companyId) {
          navigate('/subscription');
          return;
        }

        const companyDoc = await getDoc(doc(db, 'companies', companyId));
        if (!companyDoc.exists()) {
          navigate('/subscription');
          return;
        }

        const companyData = companyDoc.data();

        setUserCount(companyData.shiftsUserLimit || companyData.userLimit || 1);

        const bmcStatus = companyData.subscriptionStatus;
        if (['active', 'trialing'].includes(bmcStatus)) {
          setBundleEligible(true);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading company data:', err);
        setError(t('subscription.checkoutLoadFailed', 'Failed to load company data.'));
        setLoading(false);
      }
    };

    loadCompanyData();
  }, [companyId, navigate, t]);

  const adjustUserCount = (delta) => {
    setUserCount((prev) => Math.max(1, Math.min(1000, (prev || 1) + delta)));
  };

  const handleCheckout = async () => {
    const safeUserCount = Math.max(1, Math.min(1000, parseInt(userCount, 10) || 1));
    setUserCount(safeUserCount);
    setRedirecting(true);
    setError('');

    try {
      const createCheckoutSession = httpsCallable(functions, 'shifts_createCheckoutSession');
      const result = await createCheckoutSession({
        companyId,
        userCount: safeUserCount,
        isBundle: bundleEligible,
        successUrl: `${window.location.origin}/checkout-success`,
        cancelUrl: `${window.location.origin}/subscription`,
      });

      if (result.data?.url) {
        window.location.href = result.data.url;
      } else {
        setError(t('subscription.checkoutSessionFailed', 'Failed to create checkout session.'));
        setRedirecting(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || t('subscription.checkoutFailed', 'Checkout failed. Please try again.'));
      setRedirecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-600">
          <Loader size={20} className="animate-spin" />
          {t('subscription.loadingCheckout', 'Loading your checkout...')}
        </div>
      </div>
    );
  }

  if (!userCount || !companyId) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-2">
            <img src="/logo.png" alt="BuildMyShifts" className="w-10 h-10 object-contain" />
            <h1 className="text-2xl font-bold text-gray-900">
              Build My <span className="text-primary">Shifts</span>
            </h1>
          </div>
          <p className="text-gray-500 text-sm">{t('subscription.confirmSubscription', 'Confirm your subscription')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <CreditCard size={24} className="text-primary" />
            <h2 className="text-xl font-bold text-gray-900">{t('subscription.subscribeTitle', 'Subscribe')}</h2>
          </div>

          <div className="bg-gray-50 rounded-xl p-5 mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Users size={18} className="text-gray-500" />
                <span className="font-medium text-gray-900">{t('subscription.numberOfUsers', 'Number of users')}</span>
              </div>
            </div>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => adjustUserCount(-1)}
                disabled={userCount <= 1}
                className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Minus size={18} />
              </button>
              <input
                type="number"
                value={userCount}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1 && v <= 1000) setUserCount(v);
                }}
                className="w-20 text-center text-2xl font-bold text-gray-900 bg-transparent border-b-2 border-primary/30 focus:border-primary outline-none"
                min={1}
                max={1000}
              />
              <button
                onClick={() => adjustUserCount(1)}
                disabled={userCount >= 1000}
                className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Plus size={18} />
              </button>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-5 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">
                BuildMyShifts {bundleEligible ? `(${t('subscription.bundle', 'Bundle')})` : `(${t('subscription.standalone', 'Standalone')})`}
              </span>
              <span className="text-sm text-gray-600">{formatPrice(pricePerUser)}/user/mo</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">{t('subscription.usersLabel', 'Users')}</span>
              <span className="text-sm font-medium text-gray-900">×{userCount}</span>
            </div>
            <div className="flex items-center justify-between pt-3 mt-2 border-t border-gray-200">
              <span className="font-medium text-gray-900">{t('subscription.totalMonthly', 'Total monthly')}</span>
              <span className="text-2xl font-bold text-gray-900">{formatPrice(totalAmount)}/mo</span>
            </div>
            {bundleEligible && (
              <div className="mt-3 bg-green-50 text-green-700 text-sm px-3 py-1.5 rounded-lg">
                {t('subscription.bundleDetected', 'BuildMyChecks subscription detected — bundle pricing applied!')}
              </div>
            )}
          </div>

          <ul className="space-y-2 mb-6">
            {[
              t('subscription.featureUnlimitedRotas', 'Unlimited rotas & shifts'),
              t('subscription.featureAiAutofill', 'AI auto-fill'),
              t('subscription.featureSwapManagement', 'Swap management'),
              t('subscription.featureAnalytics', 'Analytics'),
              t('subscription.featureWtdCompliance', 'WTD compliance'),
            ].map((feature, index) => (
              <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                <Shield size={14} className="text-green-500" /> {feature}
              </li>
            ))}
          </ul>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4">{error}</div>
          )}

          <button
            onClick={handleCheckout}
            disabled={redirecting}
            className="w-full bg-primary text-white rounded-lg py-3 font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {redirecting ? <Loader size={18} className="animate-spin" /> : <CreditCard size={18} />}
            {redirecting
              ? t('subscription.redirectingToStripe', 'Redirecting to Stripe...')
              : t('subscription.proceedToPayment', 'Proceed to payment — {{amount}}/mo', {
                  amount: formatPrice(totalAmount),
                })}
          </button>

          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-400">
            <Shield size={12} />
            {t('subscription.securePaymentCancelAnytime', 'Secure payment via Stripe. Cancel anytime.')}
          </div>

          <div className="mt-4 flex justify-center">
            <button
              onClick={() => navigate('/subscription')}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              {t('subscription.backToSubscription', '← Back to subscription')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
